import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, Stack, Switch, TextField, Typography } from "@mui/material";
import { useHd } from "../HelpdeskContext";
import { crud, errorMessage } from "../api";
import { T, TYPE, primaryButtonSx } from "../../user/complaints/shared";

/** Loads a config collection and exposes create/update/remove with toasts. */
export const useCrud = (path, params) => {
  const ctx = useHd();
  const api = crud(path);
  const [rows, setRows] = useState(null);
  const key = JSON.stringify(params || {});
  const load = useCallback(async () => {
    try {
      setRows(await api.list(params));
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
      setRows([]);
    }
  }, [path, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [load]);
  const wrap = (fn, msg) => async (...args) => {
    try {
      const r = await fn(...args);
      if (msg) ctx.notify(msg);
      await load();
      ctx.reload();
      return r || true;
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
      return false;
    }
  };
  return {
    rows, load,
    create: wrap(api.create, "Saved"),
    update: wrap(api.update, "Saved"),
    remove: wrap(api.remove, "Deleted"),
  };
};

export const Section = ({ title, description, children, action }) => (
  <Box sx={{ mb: 4 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle, color: T.ink }}>{title}</Typography>
        {description && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mt: 0.25, maxWidth: 760 }}>{description}</Typography>}
      </Box>
      {action}
    </Stack>
    {children}
  </Box>
);

export const Toggle = ({ label, checked, onChange, disabled, help }) => (
  <Box sx={{ mb: 0.5 }}>
    <FormControlLabel control={<Switch size="small" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />} label={<Typography sx={{ fontSize: TYPE.small }}>{label}</Typography>} />
    {help && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, ml: 5.5, mt: -0.5 }}>{help}</Typography>}
  </Box>
);

export const Check = ({ label, checked, onChange }) => (
  <FormControlLabel control={<Checkbox size="small" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} />} label={<Typography sx={{ fontSize: TYPE.small }}>{label}</Typography>} />
);

export const NumberField = ({ label, value, onChange, min = 0, max, helperText, sx }) => (
  <TextField size="small" type="number" label={label} value={value ?? ""} inputProps={{ min, max }} helperText={helperText}
    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} sx={{ minWidth: 180, ...sx }} />
);

export const SaveBar = ({ dirty, saving, onSave, onReset }) =>
  dirty ? (
    <Box sx={{ position: "sticky", bottom: 0, py: 1.5, bgcolor: "#fff", borderTop: `1px solid ${T.border}`, display: "flex", gap: 1, justifyContent: "flex-end", zIndex: 2 }}>
      <Button onClick={onReset}>Discard</Button>
      <Button variant="contained" sx={primaryButtonSx} onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
    </Box>
  ) : null;

export const COLORS = ["#2E90FA", "#7A5AF8", "#F79009", "#EAAA08", "#98A2B3", "#12B76A", "#475467", "#F04438", "#EE46BC", "#0BA5EC"];

export const ColorPicker = ({ value, onChange }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    {COLORS.map((c) => (
      <Box key={c} onClick={() => onChange(c)} sx={{ width: 20, height: 20, borderRadius: "6px", bgcolor: c, cursor: "pointer", outline: value?.toLowerCase() === c.toLowerCase() ? `2px solid ${T.ink}` : "none", outlineOffset: 1 }} />
    ))}
    <TextField size="small" value={value || ""} onChange={(e) => onChange(e.target.value)} sx={{ width: 100, ml: 1 }} />
  </Stack>
);
